import { FC, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'components/Core/Button/Button';
import { AvatarIcon } from 'components/Core/AvatarIcon/AvatarIcon';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { updateImageUrl } from 'utils/convertString';
import { getCurrencySymbolFromCountry } from 'utils/getCurrencySymbol';
import { downloadCsv } from 'utils/csvExport';
import {
  addCoverageHistory,
  clearCoverageHistory,
  ICoverageHistoryEntry,
  loadCoverageHistory,
} from 'utils/searchCoverageHistory';
import { ICoverageCoordinate, ICoverageProduct } from 'stores/searchCoverage/types';
import { searchCoverageTranslations } from './translations';
import { SearchCoverageTransFields } from './types';
import styles from './searchCoverage.module.scss';

const RADIUS_KM = 1;
const LIMIT = 15;
const CARD_IMG = 64;

/** Splits pasted text into phrases by newline or comma (spaces kept inside a phrase); dedupes, keeps order. */
const parsePhrases = (text: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  text
    .split(/[\n,]+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((p) => {
      if (!seen.has(p)) {
        seen.add(p);
        out.push(p);
      }
    });
  return out;
};

/**
 * Extracts every number from pasted text and pairs them as (lat, lng). Robust to any
 * separator (newline / comma / space) since coordinates themselves contain commas.
 * Out-of-range pairs and a trailing unpaired number are reported separately.
 */
const parseCoordinates = (text: string): { coordinates: ICoverageCoordinate[]; invalidCount: number } => {
  const numbers = (text.match(/-?\d+(?:\.\d+)?/g) || []).map(Number);
  const coordinates: ICoverageCoordinate[] = [];
  let invalidCount = 0;
  for (let i = 0; i + 1 < numbers.length; i += 2) {
    const latitude = numbers[i];
    const longitude = numbers[i + 1];
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
      invalidCount += 1;
      continue;
    }
    coordinates.push({ latitude, longitude });
  }
  if (numbers.length % 2 !== 0) invalidCount += 1;
  return { coordinates, invalidCount };
};

const productImage = (p: ICoverageProduct): string => p.imageUrl || p.image || '';
const productPrice = (p: ICoverageProduct): number =>
  typeof p.discountPrice === 'number' && p.discountPrice > 0 ? p.discountPrice : Number(p.price ?? 0);

const formatHistoryTime = (at: number): string => new Date(at).toLocaleString();

const SearchCoverage: FC = () => {
  const { t } = useMyTranslations<SearchCoverageTransFields>([searchCoverageTranslations.name]);
  const {
    searchCoverageStore: { coverageData, loading, runCoverage, clearCoverage },
    mainStore: { user },
    alertStore: { showAlert },
  } = useStore();

  const [phrasesText, setPhrasesText] = useState('');
  const [coordsText, setCoordsText] = useState('');
  const [history, setHistory] = useState<ICoverageHistoryEntry[]>(() => loadCoverageHistory());

  const selectedCountry = user?.selectedCountryCode?.countryCode || '';

  const parsedPhrases = useMemo(() => parsePhrases(phrasesText), [phrasesText]);
  const parsedCoords = useMemo(() => parseCoordinates(coordsText), [coordsText]);

  const currency = useMemo(
    () => getCurrencySymbolFromCountry(coverageData?.countryCode || selectedCountry),
    [coverageData, selectedCountry]
  );

  // React to the admin switching country: drop stale results from another country.
  useEffect(() => {
    clearCoverage();
  }, [selectedCountry, clearCoverage]);

  const onRun = async () => {
    if (parsedPhrases.length === 0 || parsedCoords.coordinates.length === 0) {
      showAlert(t('emptyForm'));
      return;
    }
    await runCoverage({
      phrases: parsedPhrases,
      coordinates: parsedCoords.coordinates,
      radius: RADIUS_KM,
      limit: LIMIT,
    });
    setHistory(
      addCoverageHistory(
        { countryCode: selectedCountry, phrases: parsedPhrases, coordinates: parsedCoords.coordinates },
        Date.now()
      )
    );
  };

  const onClear = () => {
    setPhrasesText('');
    setCoordsText('');
    clearCoverage();
  };

  const loadFromHistory = (entry: ICoverageHistoryEntry) => {
    setPhrasesText(entry.phrases.join('\n'));
    setCoordsText(entry.coordinates.map((c) => `${c.latitude}, ${c.longitude}`).join('\n'));
  };

  const onClearHistory = () => setHistory(clearCoverageHistory());

  const onExport = () => {
    if (!coverageData) return;
    const rows: Array<Array<unknown>> = [
      [
        'country',
        'latitude',
        'longitude',
        'phrase',
        'foundTotal',
        '#',
        'source',
        'imageUrl',
        'name',
        'price',
        'discountPrice',
      ],
    ];
    coverageData.results.forEach((coord) => {
      coord.phrases.forEach((ph) => {
        if (ph.products.length === 0) {
          rows.push([
            coverageData.countryCode,
            coord.latitude,
            coord.longitude,
            ph.phrase,
            ph.total,
            '',
            '',
            '',
            ph.error || '',
            '',
            '',
          ]);
          return;
        }
        ph.products.forEach((p, idx) => {
          rows.push([
            coverageData.countryCode,
            coord.latitude,
            coord.longitude,
            ph.phrase,
            ph.total,
            idx + 1,
            p.fromNewspaper ? 'newspaper' : 'regular',
            productImage(p),
            p.name ?? '',
            p.price ?? '',
            p.discountPrice ?? '',
          ]);
        });
      });
    });
    downloadCsv(`coverage_${coverageData.countryCode}.csv`, rows);
  };

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.headerTitle}>{t('title')}</h2>
          <p className={styles.subtitle}>{t('subtitle', { limit: LIMIT, radius: RADIUS_KM })}</p>
        </div>
        <span className={styles.country}>
          {t('country')}: {selectedCountry || '—'}
        </span>
      </header>

      <div className={styles.form}>
        <div className={styles.column}>
          <span className={styles.columnTitle}>
            {t('phrases')} <span className={styles.count}>· {parsedPhrases.length}</span>
          </span>
          <span className={styles.hint}>{t('phrasesHint')}</span>
          <textarea
            className={styles.textarea}
            value={phrasesText}
            placeholder={t('phrasePlaceholder')}
            onChange={(e) => setPhrasesText(e.target.value)}
          />
          {parsedPhrases.length > 0 && (
            <div className={styles.chips}>
              {parsedPhrases.map((p, i) => (
                <span className={styles.chip} key={`ph-${i}`}>
                  {p}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.column}>
          <span className={styles.columnTitle}>
            {t('coordinates')} <span className={styles.count}>· {parsedCoords.coordinates.length}</span>
          </span>
          <span className={styles.hint}>{t('coordinatesHint')}</span>
          <textarea
            className={styles.textarea}
            value={coordsText}
            placeholder={t('coordinatePlaceholder')}
            onChange={(e) => setCoordsText(e.target.value)}
          />
          {parsedCoords.coordinates.length > 0 && (
            <div className={styles.chips}>
              {parsedCoords.coordinates.map((c, i) => (
                <span className={styles.chip} key={`co-${i}`}>
                  {c.latitude}, {c.longitude}
                </span>
              ))}
            </div>
          )}
          {parsedCoords.invalidCount > 0 && (
            <span className={styles.warn}>{t('coordinatesIgnored', { count: parsedCoords.invalidCount })}</span>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <Button onClick={onRun} title={loading ? t('running') : t('run')} isDisabled={loading} />
        <Button onClick={onClear} title={t('clear')} isDisabled={loading} />
        {coverageData && <Button onClick={onExport} title={t('exportCsv')} isDisabled={loading} />}
        {loading && (
          <span className={styles.loader}>
            <span className={styles.spinner} />
            {t('running')}
          </span>
        )}
      </div>

      {history.length > 0 && (
        <div className={styles.history}>
          <div className={styles.historyHead}>
            <span className={styles.columnTitle}>{t('history')}</span>
            <button className={styles.linkBtn} type='button' onClick={onClearHistory}>
              {t('clearHistory')}
            </button>
          </div>
          <ul className={styles.historyList}>
            {history.map((entry) => (
              <li className={styles.historyItem} key={entry.id}>
                <button className={styles.historyBtn} type='button' onClick={() => loadFromHistory(entry)}>
                  <span className={styles.historyTime}>{formatHistoryTime(entry.at)}</span>
                  <span className={styles.historyMeta}>
                    {entry.countryCode} · {entry.phrases.length} × {entry.coordinates.length}
                  </span>
                  <span className={styles.historyPhrases}>{entry.phrases.join(', ')}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!coverageData ? (
        !loading && <p className={styles.placeholder}>{t('noResults')}</p>
      ) : (
        <div className={styles.results}>
          {coverageData.results.map((coord, ci) => (
            <div className={styles.coordBlock} key={`res-${ci}`}>
              <div className={styles.coordHeader}>
                {coord.latitude}, {coord.longitude}
              </div>
              {coord.phrases.map((ph, pi) => (
                <div className={styles.phraseRow} key={`ph-${ci}-${pi}`}>
                  <div className={styles.phraseLabel}>
                    <span className={styles.phraseName}>{ph.phrase}</span>
                    {ph.error ? (
                      <span className={styles.phraseError}>{ph.error}</span>
                    ) : (
                      <span className={styles.phraseCount}>
                        {t('found')}: {ph.total} · {t('shown')}: {ph.products.length}
                      </span>
                    )}
                  </div>
                  {ph.products.length === 0 ? (
                    <span className={styles.empty}>{t('noProducts')}</span>
                  ) : (
                    <div className={styles.cards}>
                      {ph.products.map((p, idx) => (
                        <div className={styles.card} key={`p-${idx}-${p.id ?? idx}`}>
                          {p.fromNewspaper && <span className={styles.badge}>{t('flyer')}</span>}
                          <AvatarIcon
                            avatar={productImage(p) ? updateImageUrl(productImage(p), CARD_IMG * 2) : ''}
                            size={CARD_IMG}
                            name={p.name || ''}
                          />
                          <span className={styles.cardName}>{p.name}</span>
                          <span className={styles.cardPrice}>
                            {productPrice(p)} {currency}
                            {typeof p.discountPrice === 'number' &&
                              p.discountPrice > 0 &&
                              typeof p.price === 'number' && (
                                <span className={styles.cardOldPrice}>
                                  {p.price} {currency}
                                </span>
                              )}
                          </span>
                          <div className={styles.tooltip}>
                            <span className={styles.tooltipTitle}>{t('keywords')}</span>
                            {p.keywords && p.keywords.length > 0 ? (
                              <div className={styles.tooltipChips}>
                                {p.keywords.map((kw, ki) => (
                                  <span className={styles.tooltipChip} key={`kw-${idx}-${ki}`}>
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className={styles.tooltipEmpty}>{t('noKeywords')}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </main>
  );
};

export default observer(SearchCoverage);
